import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = 'https://cineshorts-ai-production.up.railway.app';

// eslint-disable-next-line no-unused-vars
function CountdownTimer({ createdAt, hoursLeft }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const created = new Date(createdAt);
      const deadline = new Date(created.getTime() + 24 * 60 * 60 * 1000);
      const now = new Date();
      const diff = deadline - now;
      if (diff <= 0) {
        setTimeLeft('⏰ Auto-approved!');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s left`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [createdAt]);

  return (
    <span className={`text-xs font-bold ${hoursLeft <= 2 ? 'text-red-500' : 'text-orange-500'}`}>
      ⏳ {timeLeft}
    </span>
  );
}

// ── User Detail Modal ─────────────────────────────────────────────
function UserDetailModal({ user, onClose }) {
  if (!user) return null;

  const planColors = {
    free: '#6b7280',
    pro_basic: '#f59e0b',
    pro_premium: '#8b5cf6',
    owner: '#ef4444'
  };

  const planColor = planColors[user.plan] || '#6b7280';

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px'
    }}>
      <div style={{
        background: '#fff', borderRadius: '20px', padding: '28px',
        maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1f2937' }}>👤 User Details</h2>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>

        {/* Avatar & Name */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `${planColor}22`, border: `3px solid ${planColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', margin: '0 auto 12px' }}>
            👤
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#1f2937' }}>{user.full_name}</h3>
          <span style={{ background: `${planColor}22`, color: planColor, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>
            {user.plan === 'pro_basic' ? '⭐ Pro Basic' : user.plan === 'pro_premium' ? '💎 Pro Premium' : user.plan === 'owner' ? '👑 Owner' : '🆓 Free'}
          </span>
        </div>

        {/* Details Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <DetailBox icon="📱" label="Phone" value={user.phone} />
          <DetailBox icon="📧" label="Email" value={user.email || 'N/A'} />
          <DetailBox icon="🎂" label="Date of Birth" value={user.dob || 'N/A'} />
          <DetailBox icon="📅" label="Joined" value={user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} />
          <DetailBox icon="🎬" label="Movies Used" value={`${user.movies_used_today || 0} today`} />
          <DetailBox icon="✂️" label="Shorts Used" value={`${user.shorts_used_today || 0} today`} />
          <DetailBox icon="📆" label="Plan Expiry" value={user.subscription_expiry || 'N/A'} />
          <DetailBox icon="🚫" label="Status" value={user.is_banned ? '❌ Banned' : '✅ Active'} />
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '12px', background: '#0d9488', border: 'none', borderRadius: '12px', color: '#fff', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}>
          Close
        </button>
      </div>
    </div>
  );
}

function DetailBox({ icon, label, value }) {
  return (
    <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '12px' }}>
      <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{icon} {label}</p>
      <p style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', wordBreak: 'break-all' }}>{value}</p>
    </div>
  );
}

// ── Main Admin Dashboard ──────────────────────────────────────────
function AdminDashboard({ onBack }) {
  const [adminPhone, setAdminPhone] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [updateMsg, setUpdateMsg] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/dashboard`, {
        admin_phone: adminPhone,
        admin_password: adminPassword
      });
      setData(res.data);
      setLoggedIn(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed!');
    }
    setLoading(false);
  };

  const handleConfirm = async (userPhone, plan) => {
    setUpdateMsg('');
    try {
      const res = await axios.post(`${API}/admin/update-plan`, {
        admin_phone: adminPhone,
        admin_password: adminPassword,
        user_phone: userPhone,
        plan: plan
      });
      setUpdateMsg('✅ ' + res.data.message);
      handleLogin();
    } catch (err) {
      setUpdateMsg(err.response?.data?.detail || 'Error!');
    }
  };

  const handleDecline = async (transactionId, phone) => {
    setUpdateMsg('');
    try {
      await axios.post(`${API}/admin/decline-payment`, {
        admin_phone: adminPhone,
        admin_password: adminPassword,
        transaction_id: transactionId
      });
      setUpdateMsg(`❌ ${phone} payment declined!`);
      handleLogin();
    } catch (err) {
      setUpdateMsg('Payment declined successfully!');
      handleLogin();
    }
  };

  const handlePlanUpdate = async (userPhone, plan) => {
    setUpdateMsg('');
    try {
      const res = await axios.post(`${API}/admin/update-plan`, {
        admin_phone: adminPhone,
        admin_password: adminPassword,
        user_phone: userPhone,
        plan: plan
      });
      setUpdateMsg('✅ ' + res.data.message);
      handleLogin();
    } catch (err) {
      setUpdateMsg(err.response?.data?.detail || 'Error!');
    }
  };

  const handleBan = async (userPhone, isBanned) => {
    try {
      const res = await axios.post(`${API}/admin/ban-user`, {
        admin_phone: adminPhone,
        admin_password: adminPassword,
        user_phone: userPhone,
        is_banned: isBanned
      });
      setUpdateMsg('✅ ' + res.data.message);
      handleLogin();
    } catch (err) {
      setUpdateMsg(err.response?.data?.detail || 'Error!');
    }
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-teal-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full mx-4">
          <h1 className="text-2xl font-bold text-teal-600 text-center mb-6">🔐 Admin Login</h1>
          <input type="text" placeholder="Admin Phone" value={adminPhone} onChange={(e) => setAdminPhone(e.target.value)} className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 mb-3 focus:border-teal-400 outline-none" />
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Admin Password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-teal-400 outline-none"
              style={{ paddingRight: '48px' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#9ca3af' }}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-4">{error}</div>}
          <button onClick={handleLogin} disabled={loading} className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 rounded-xl">
            {loading ? 'Loading...' : 'Login'}
          </button>
          <button onClick={onBack} className="w-full mt-3 text-gray-400 hover:text-gray-600 py-2">← Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-teal-50">
      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}

      <div className="bg-white shadow-sm px-6 py-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-teal-600">🛡️ Admin Dashboard</h1>
        <button onClick={onBack} className="text-red-400 hover:text-red-600 font-semibold">Logout</button>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">

        {updateMsg && (
          <div className="bg-teal-50 border border-teal-200 text-teal-700 px-4 py-3 rounded-xl mb-6 font-semibold">
            {updateMsg}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8 md:grid-cols-3">
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <p className="text-3xl font-bold text-teal-600">{data.stats.total_users}</p>
            <p className="text-sm text-gray-500 mt-1">Total Users</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <p className="text-3xl font-bold text-blue-500">{data.stats.free_users}</p>
            <p className="text-sm text-gray-500 mt-1">Free Users</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <p className="text-3xl font-bold text-yellow-500">{data.stats.pro_basic_users}</p>
            <p className="text-sm text-gray-500 mt-1">Pro Basic</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <p className="text-3xl font-bold text-purple-500">{data.stats.pro_premium_users}</p>
            <p className="text-sm text-gray-500 mt-1">Pro Premium</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <p className="text-3xl font-bold text-red-500">{data.stats.pending_payments}</p>
            <p className="text-sm text-gray-500 mt-1">Pending Payments</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
            <p className="text-3xl font-bold text-green-500">PKR {data.stats.total_revenue.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-1">Total Revenue</p>
          </div>
        </div>

        {/* Pending Payments */}
        {data.pending_payments && data.pending_payments.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-700 mb-4">⏳ Pending Payments ({data.pending_payments.length})</h2>
            {data.pending_payments.map((t, i) => (
              <div key={i} className="border border-yellow-200 rounded-xl p-4 mb-3 bg-yellow-50">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-gray-700">{t.phone}</p>
                    <p className="text-sm text-gray-500">{t.plan} — PKR {t.amount} — {t.payment_method}</p>
                    <p className="text-xs text-gray-400">TXN: {t.transaction_id}</p>
                    <p className="text-xs text-gray-400">📅 {new Date(t.created_at).toLocaleString('en-US')}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => handleConfirm(t.phone, t.plan)} className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-xl text-sm font-bold">
                      ✅ Confirm
                    </button>
                    <button onClick={() => handleDecline(t.id, t.phone)} className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-bold">
                      ❌ Decline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Declined Payments */}
        {data.declined_payments && data.declined_payments.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-700 mb-4">❌ Declined Payments ({data.declined_payments.length})</h2>
            {data.declined_payments.map((t, i) => (
              <div key={i} className="border border-red-200 rounded-xl p-4 mb-3 bg-red-50">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-gray-700">{t.phone}</p>
                    <p className="text-sm text-gray-500">{t.plan} — PKR {t.amount} — {t.payment_method}</p>
                    <p className="text-xs text-gray-400">TXN: {t.transaction_id}</p>
                    <p className="text-xs text-gray-400">📅 Submit: {new Date(t.created_at).toLocaleString('en-US')}</p>
                    <p className="text-xs text-red-400">❌ Declined: {t.declined_at ? new Date(t.declined_at).toLocaleString('en-US') : 'N/A'}</p>
                  </div>
                  <button onClick={() => handleConfirm(t.phone, t.plan)} className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-xl text-sm font-bold">
                    🔄 Confirm Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* All Transactions History */}
        {data.all_transactions && data.all_transactions.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-700 mb-4">📋 All Transactions History</h2>
            {data.all_transactions.map((t, i) => (
              <div key={i} className={`border rounded-xl p-4 mb-3 ${t.status === 'confirmed' ? 'border-green-200 bg-green-50' : t.status === 'declined' ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-gray-700">{t.phone}</p>
                    <p className="text-sm text-gray-500">{t.plan} — PKR {t.amount} — {t.payment_method}</p>
                    <p className="text-xs text-gray-400">TXN: {t.transaction_id}</p>
                    <p className="text-xs text-gray-400">📅 {new Date(t.created_at).toLocaleString('en-US')}</p>
                    {t.auto_approved && <p className="text-xs text-blue-500">🤖 Auto-approved</p>}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${t.status === 'confirmed' ? 'bg-green-200 text-green-700' : t.status === 'declined' ? 'bg-red-200 text-red-700' : 'bg-yellow-200 text-yellow-700'}`}>
                    {t.status === 'confirmed' ? '✅ Confirmed' : t.status === 'declined' ? '❌ Declined' : '⏳ Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Users List */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-700 mb-4">👥 All Users ({data.users.length})</h2>
          {data.users.map((u, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-4 mb-3">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                  <p className="font-bold text-gray-700">{u.full_name}</p>
                  <p className="text-sm text-gray-500">{u.phone}</p>
                  {u.email && <p className="text-xs text-gray-400">📧 {u.email}</p>}
                  <p className="text-xs text-teal-600 font-semibold mt-1">{u.plan}</p>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  {/* ← Details Button */}
                  <button
                    onClick={() => setSelectedUser(u)}
                    style={{ background: '#0d9488', color: '#fff', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    👁️ Details
                  </button>
                  <select onChange={(e) => handlePlanUpdate(u.phone, e.target.value)} defaultValue={u.plan} className="border border-gray-200 rounded-lg px-2 py-1 text-sm">
                    <option value="free">Free</option>
                    <option value="pro_basic">Pro Basic</option>
                    <option value="pro_premium">Pro Premium</option>
                    <option value="owner">Owner</option>
                  </select>
                  <button onClick={() => handleBan(u.phone, !u.is_banned)} className={`px-3 py-1 rounded-lg text-sm font-bold ${u.is_banned ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                    {u.is_banned ? 'Unban' : 'Ban'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

export default AdminDashboard;
